import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

const API = '/api';

const REASON_LABELS: Record<string, { label: string; type: 'cancel' | 'return'; color: string; bg: string }> = {
  'CANCELLED_BY_BUYER':    { label: 'Anulowane przez kupującego', type: 'cancel', color: '#dc2626', bg: '#fef2f2' },
  'PRODUCT_NOT_AVAILABLE': { label: 'Anulowane — brak produktu',  type: 'cancel', color: '#d97706', bg: '#fffbeb' },
  'NOT_COLLECTED':         { label: 'Nieodebrane',                type: 'cancel', color: '#7c3aed', bg: '#faf5ff' },
  'REFUND':                { label: 'Zwrot towaru',               type: 'return', color: '#1d4ed8', bg: '#eff6ff' },
  'COMPLAINT':             { label: 'Reklamacja',                  type: 'return', color: '#0369a1', bg: '#f0f9ff' },
  'PAID_VALUE_TOO_LOW':    { label: 'Za niska wpłata',            type: 'return', color: '#6b7280', bg: '#f9fafb' },
  'OVERPAID':              { label: 'Zwrot nadpłaty',             type: 'return', color: '#15803d', bg: '#f0fdf4' },
};

const STATUS_LABELS: Record<string, string> = {
  'SUCCESS': '✓ Zaksięgowany',
  'PENDING': '⏳ W trakcie',
  'CANCELLED': '✗ Anulowany',
  'NEW': '🆕 Nowy',
};

interface RefundItem {
  id: string;
  payment_id: string;
  order_id: string;
  reason: string;
  status: string;
  created_at: string;
  total_amount: number;
  total_currency: string;
  buyer_login: string;
  buyer_email: string;
  buyer_data: any;
  items: any[];
  account_id: string;
  account_name: string;
}

export default function PaymentRefunds({ accounts, onBack }: { accounts: { id: string; name: string }[]; onBack: () => void }) {
  const [items, setItems] = useState<RefundItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [filterReasons, setFilterReasons] = useState<Set<string>>(new Set());
  const [filterAccount, setFilterAccount] = useState('');
  const _defaultFrom = () => {
    const d = new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() - 1);
    return d.toISOString().split('T')[0];
  };
  const [dateFrom, setDateFrom] = useState(_defaultFrom());
  const [dateTo, setDateTo] = useState('');
  const [search, setSearch] = useState('');
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [page, setPage] = useState(0);
  const [syncMsg, setSyncMsg] = useState('');
  const PAGE_SIZE = 50;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const printRef = useRef<HTMLDivElement>(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchRefunds(); }, [filterReasons, filterAccount, dateFrom, dateTo, page]);

  const fetchRefunds = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterReasons.size > 0) params.set('reason', Array.from(filterReasons).join(','));
      if (filterAccount) params.set('account_id', filterAccount);
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      if (search.trim()) params.set('search', search.trim());
      params.set('offset', String(page * PAGE_SIZE));
      params.set('limit', String(PAGE_SIZE));
      const res = await axios.get(`${API}/payments/refunds/list?${params}`);
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch(e) { console.error(e); }
    setLoading(false);
  };

  const syncRefunds = async () => {
    setSyncing(true);
    setSyncMsg('');
    try {
      const res = await axios.post(`${API}/payments/refunds/sync`, {}, { timeout: 300000 });
      setSyncMsg(`✓ Zsynchronizowano: +${res.data.added} nowych, ${res.data.updated} zaktualizowanych${res.data.errors?.length ? ` · Błędy: ${res.data.errors.join(', ')}` : ''}`);
      fetchRefunds();
    } catch(e: any) {
      setSyncMsg('❌ Błąd synchronizacji');
    }
    setSyncing(false);
  };

  const toggleReason = (reason: string) => {
    setFilterReasons(prev => {
      const next = new Set(prev);
      if (next.has(reason)) next.delete(reason);
      else next.add(reason);
      return next;
    });
    setPage(0);
  };

  const toggleSelect = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    if (selected.size === items.length) setSelected(new Set());
    else setSelected(new Set(items.map(i => i.id)));
  };

  const formatDate = (d: string) => {
    if (!d) return '—';
    return new Date(d).toLocaleString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const generatePrintContent = (refund: RefundItem): string => {
    const r = REASON_LABELS[refund.reason] || { label: refund.reason, type: 'return', color: '#374151', bg: '#f9fafb' };
    const itemsHtml = (refund.items || []).map(item => `
      <tr>
        <td style="padding:6px 8px;border:1px solid #e5e7eb;font-size:12px">${item.name || '—'}</td>
        <td style="padding:6px 8px;border:1px solid #e5e7eb;font-size:12px;text-align:center">${item.quantity || 1}</td>
        <td style="padding:6px 8px;border:1px solid #e5e7eb;font-size:12px;text-align:right">${item.price || '—'} ${item.currency || 'PLN'}</td>
      </tr>`).join('');
    return `
      <div style="font-family:Arial,sans-serif;max-width:700px;margin:0 auto;padding:32px;border:2px solid #1d4ed8;border-radius:8px">
        <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:24px">
          <div>
            <div style="font-size:22px;font-weight:700;color:#1d4ed8">Potwierdzenie zwrotu płatności</div>
            <div style="font-size:13px;color:#6b7280;margin-top:4px">OPENkontakt · openkontakt.com</div>
          </div>
          <div style="text-align:right">
            <div style="font-size:12px;color:#6b7280">Data wydruku:</div>
            <div style="font-size:13px;font-weight:600">${new Date().toLocaleString('pl-PL')}</div>
          </div>
        </div>

        <table style="width:100%;border-collapse:collapse;margin-bottom:20px">
          <tr>
            <td style="padding:8px 12px;background:#f8fafc;border:1px solid #e5e7eb;font-size:12px;color:#6b7280;width:40%">Identyfikator zwrotu:</td>
            <td style="padding:8px 12px;background:#fff;border:1px solid #e5e7eb;font-size:12px;font-family:monospace">${refund.id}</td>
          </tr>
          <tr>
            <td style="padding:8px 12px;background:#f8fafc;border:1px solid #e5e7eb;font-size:12px;color:#6b7280">Identyfikator płatności:</td>
            <td style="padding:8px 12px;background:#fff;border:1px solid #e5e7eb;font-size:12px;font-family:monospace">${refund.payment_id || '—'}</td>
          </tr>
          <tr>
            <td style="padding:8px 12px;background:#f8fafc;border:1px solid #e5e7eb;font-size:12px;color:#6b7280">Identyfikator zamówienia:</td>
            <td style="padding:8px 12px;background:#fff;border:1px solid #e5e7eb;font-size:12px;font-family:monospace">${refund.order_id || '—'}</td>
          </tr>
          <tr>
            <td style="padding:8px 12px;background:#f8fafc;border:1px solid #e5e7eb;font-size:12px;color:#6b7280">Data zwrotu płatności:</td>
            <td style="padding:8px 12px;background:#fff;border:1px solid #e5e7eb;font-size:12px;font-weight:600">${formatDate(refund.created_at)}</td>
          </tr>
          <tr>
            <td style="padding:8px 12px;background:#f8fafc;border:1px solid #e5e7eb;font-size:12px;color:#6b7280">Status zwrotu:</td>
            <td style="padding:8px 12px;background:#fff;border:1px solid #e5e7eb;font-size:12px;font-weight:600;color:#15803d">${STATUS_LABELS[refund.status] || refund.status}</td>
          </tr>
          <tr>
            <td style="padding:8px 12px;background:#f8fafc;border:1px solid #e5e7eb;font-size:12px;color:#6b7280">Powód zwrotu:</td>
            <td style="padding:8px 12px;background:${r.bg};border:1px solid #e5e7eb;font-size:12px;font-weight:600;color:${r.color}">${r.label}</td>
          </tr>
          <tr>
            <td style="padding:8px 12px;background:#f8fafc;border:1px solid #e5e7eb;font-size:12px;color:#6b7280">Konto Allegro:</td>
            <td style="padding:8px 12px;background:#fff;border:1px solid #e5e7eb;font-size:12px">${refund.account_name || '—'}</td>
          </tr>
        </table>

        <div style="margin-bottom:16px">
          <div style="font-size:14px;font-weight:700;color:#374151;margin-bottom:8px;padding-bottom:4px;border-bottom:2px solid #e5e7eb">Kupujący</div>
          <table style="width:100%;border-collapse:collapse">
            <tr>
              <td style="padding:6px 12px;background:#f8fafc;border:1px solid #e5e7eb;font-size:12px;color:#6b7280;width:40%">Login Allegro:</td>
              <td style="padding:6px 12px;background:#fff;border:1px solid #e5e7eb;font-size:12px;font-weight:600">${refund.buyer_login || '—'}</td>
            </tr>
            ${((refund.buyer_data?.firstName || refund.buyer_data?.lastName) ? `<tr><td style="padding:6px 12px;background:#f8fafc;border:1px solid #e5e7eb;font-size:12px;color:#6b7280;width:40%">Imię i nazwisko:</td><td style="padding:6px 12px;background:#fff;border:1px solid #e5e7eb;font-size:12px;font-weight:600">${(refund.buyer_data?.firstName||'') + ' ' + (refund.buyer_data?.lastName||'')}</td></tr>` : '')}
            ${(refund.buyer_data?.phoneNumber ? `<tr><td style="padding:6px 12px;background:#f8fafc;border:1px solid #e5e7eb;font-size:12px;color:#6b7280">Telefon:</td><td style="padding:6px 12px;background:#fff;border:1px solid #e5e7eb;font-size:12px">${refund.buyer_data.phoneNumber}</td></tr>` : '')}
            ${(refund.buyer_data?.address?.street ? `<tr><td style="padding:6px 12px;background:#f8fafc;border:1px solid #e5e7eb;font-size:12px;color:#6b7280">Adres:</td><td style="padding:6px 12px;background:#fff;border:1px solid #e5e7eb;font-size:12px">${refund.buyer_data.address.street}, ${refund.buyer_data.address.zipCode||''} ${refund.buyer_data.address.city||''}</td></tr>` : '')}
            <tr><td style="display:none"></td>
            </tr>
            <tr>
              <td style="padding:6px 12px;background:#f8fafc;border:1px solid #e5e7eb;font-size:12px;color:#6b7280">Email:</td>
              <td style="padding:6px 12px;background:#fff;border:1px solid #e5e7eb;font-size:12px">${refund.buyer_email || '—'}</td>
            </tr>
          </table>
        </div>

        <div style="margin-bottom:16px">
          <div style="font-size:14px;font-weight:700;color:#374151;margin-bottom:8px;padding-bottom:4px;border-bottom:2px solid #e5e7eb">Produkty</div>
          <table style="width:100%;border-collapse:collapse">
            <tr style="background:#f8fafc">
              <th style="padding:6px 8px;border:1px solid #e5e7eb;font-size:12px;text-align:left">Nazwa produktu</th>
              <th style="padding:6px 8px;border:1px solid #e5e7eb;font-size:12px;text-align:center">Ilość</th>
              <th style="padding:6px 8px;border:1px solid #e5e7eb;font-size:12px;text-align:right">Cena</th>
            </tr>
            ${itemsHtml || '<tr><td colspan="3" style="padding:8px;text-align:center;color:#9ca3af;font-size:12px">Brak danych produktów</td></tr>'}
          </table>
        </div>

        <div style="background:#1d4ed8;color:#fff;padding:14px 18px;border-radius:8px;display:flex;justify-content:space-between;align-items:center">
          <div style="font-size:14px;font-weight:600">KWOTA ZWROTU</div>
          <div style="font-size:22px;font-weight:700">${refund.total_amount.toFixed(2)} ${refund.total_currency}</div>
        </div>

        <div style="margin-top:20px;font-size:11px;color:#9ca3af;text-align:center;border-top:1px solid #e5e7eb;padding-top:12px">
          Dokument wygenerowany automatycznie przez system OPENkontakt · openkontakt.com<br>
          Płatności obsługiwane w ramach usługi Allegro Finance
        </div>
      </div>`;
  };

  const printSingle = (refund: RefundItem) => {
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(`<!DOCTYPE html><html><head><title>Zwrot ${refund.id}</title>
      <style>@media print { body { margin: 0; } } body { background: #fff; }</style>
      </head><body>${generatePrintContent(refund)}</body></html>`);
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); }, 300);
  };

  const printSelected = () => {
    const selectedItems = items.filter(i => selected.has(i.id));
    if (!selectedItems.length) return;
    const content = selectedItems.map((r, idx) =>
      `${generatePrintContent(r)}${idx < selectedItems.length - 1 ? '<div style="page-break-after:always"></div>' : ''}`
    ).join('');
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(`<!DOCTYPE html><html><head><title>Zwroty płatności</title>
      <style>@media print { body { margin: 0; } .page-break { page-break-after: always; } } body { background: #fff; }</style>
      </head><body>${content}</body></html>`);
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); }, 300);
  };

  const downloadZip = async () => {
    const selectedItems = items.filter(i => selected.has(i.id));
    if (!selectedItems.length) return;
    try {
      const res = await axios.post(`${API}/payments/refunds/download-zip`,
        { ids: Array.from(selected) },
        { responseType: 'blob' }
      );
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/zip' }));
      const a = document.createElement('a');
      a.href = url;
      const date = new Date().toISOString().split('T')[0];
      a.download = `zwroty_platnosci_${date}.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch(e) {
      alert('Błąd generowania ZIP. Spróbuj ponownie.');
    }
  };

    const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: '#f9fafb' }}>
      {/* Nagłówek */}
      <div style={{ background: '#fff', borderBottom: '1px solid #e5e7eb' }}>
        <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
          <button onClick={onBack} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#6b7280' }}>&#8592;</button>
          <span style={{ fontWeight: 700, fontSize: 15, flex: 1 }}>💰 Wydruki zwrotów płatności</span>
          {selected.size > 0 && (
            <>
              <button onClick={printSelected}
                style={{ padding: '6px 12px', background: '#f0fdf4', color: '#15803d',
                  border: '1px solid #bbf7d0', borderRadius: 7, fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>
                🖨️ Drukuj zaznaczone ({selected.size})
              </button>
              <button onClick={downloadZip}
                style={{ padding: '6px 12px', background: '#eff6ff', color: '#1d4ed8',
                  border: '1px solid #bfdbfe', borderRadius: 7, fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>
                📦 Pobierz ZIP ({selected.size})
              </button>
            </>
          )}
          <button onClick={syncRefunds} disabled={syncing}
            style={{ padding: '6px 14px', background: syncing ? '#e5e7eb' : '#1d4ed8', color: syncing ? '#9ca3af' : '#fff',
              border: 'none', borderRadius: 7, fontSize: 12, cursor: syncing ? 'not-allowed' : 'pointer', fontWeight: 600 }}>
            {syncing ? '⏳ Synchronizuję...' : '↻ Synchronizuj z Allegro'}
          </button>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0 16px 10px' }}>
        </div>
        {syncMsg && (
          <div style={{ fontSize: 12, padding: '6px 16px 8px', borderTop: '1px solid #f3f4f6',
            background: syncMsg.includes('❌') ? '#fef2f2' : '#f0fdf4',
            color: syncMsg.includes('❌') ? '#dc2626' : '#15803d' }}>
            {syncMsg}
          </div>
        )}

        {/* Filtry reason */}
        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 8 }}>
          <span style={{ fontSize: 11, color: '#6b7280', alignSelf: 'center', marginRight: 2 }}>Powód:</span>
          {Object.entries(REASON_LABELS).map(([key, val]) => (
            <button key={key} onClick={() => toggleReason(key)}
              style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, cursor: 'pointer', fontWeight: filterReasons.has(key) ? 600 : 400,
                border: `1px solid ${filterReasons.has(key) ? val.color : '#d1d5db'}`,
                background: filterReasons.has(key) ? val.bg : 'transparent',
                color: filterReasons.has(key) ? val.color : '#6b7280' }}>
              {val.label}
            </button>
          ))}
          {filterReasons.size > 0 && (
            <button onClick={() => setFilterReasons(new Set())}
              style={{ padding: '3px 8px', borderRadius: 20, fontSize: 11, cursor: 'pointer', border: '1px solid #d1d5db', background: 'transparent', color: '#9ca3af' }}>
              ✕ Wyczyść
            </button>
          )}
        </div>

        {/* Filtry data i wyszukiwarka */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          <input value={search} onChange={e => {
              const val = e.target.value;
              setSearch(val);
              if (searchTimer.current) clearTimeout(searchTimer.current);
              if (val.trim().length >= 3 || val.trim().length === 0) {
                searchTimer.current = setTimeout(() => { setPage(0); fetchRefunds(); }, 400);
              }
            }}
            onKeyDown={e => { if (e.key === 'Enter') { setPage(0); fetchRefunds(); } }}
            placeholder="Szukaj po loginie lub nr zamówienia..."
            style={{ flex: 1, minWidth: 180, padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12, outline: 'none' }} />
          <label style={{ fontSize: 11, color: '#6b7280' }}>Od:</label>
          <input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPage(0); }}
            style={{ padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12 }} />
          <label style={{ fontSize: 11, color: '#6b7280' }}>Do:</label>
          <input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setPage(0); }}
            style={{ padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12 }} />
          <select value={filterAccount} onChange={e => { setFilterAccount(e.target.value); setPage(0); }}
            style={{ padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12, background: '#fff' }}>
            <option value="">Wszystkie konta</option>
            {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
          <button onClick={() => { setPage(0); fetchRefunds(); }}
            style={{ padding: '6px 12px', background: '#1d4ed8', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>
            Szukaj
          </button>
        </div>
      </div>

      {/* Lista */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {loading && <div style={{ padding: 16, color: '#6b7280', fontSize: 12, textAlign: 'center' }}>Ładowanie...</div>}
        {!loading && items.length === 0 && (
          <div style={{ padding: 40, color: '#9ca3af', fontSize: 13, textAlign: 'center' }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>💰</div>
            <div>Brak zwrotów płatności.</div>
            <div style={{ marginTop: 8 }}>Kliknij "Synchronizuj z Allegro" aby pobrać dane.</div>
          </div>
        )}

        {/* Nagłówek tabeli */}
        {items.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e5e7eb' }}>
                <th style={{ padding: '8px 10px', textAlign: 'left', width: 32 }}>
                  <input type="checkbox" checked={selected.size === items.length && items.length > 0}
                    onChange={selectAll} style={{ cursor: 'pointer' }} />
                </th>
                <th style={{ padding: '8px 10px', textAlign: 'left', color: '#6b7280', fontWeight: 600 }}>Data zwrotu</th>
                <th style={{ padding: '8px 10px', textAlign: 'left', color: '#6b7280', fontWeight: 600 }}>Kupujący</th>
                <th style={{ padding: '8px 10px', textAlign: 'left', color: '#6b7280', fontWeight: 600 }}>Powód</th>
                <th style={{ padding: '8px 10px', textAlign: 'left', color: '#6b7280', fontWeight: 600 }}>Status</th>
                <th style={{ padding: '8px 10px', textAlign: 'right', color: '#6b7280', fontWeight: 600 }}>Kwota</th>
                <th style={{ padding: '8px 10px', textAlign: 'left', color: '#6b7280', fontWeight: 600 }}>Konto</th>
                <th style={{ padding: '8px 10px', textAlign: 'center', color: '#6b7280', fontWeight: 600 }}>Druk</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, idx) => {
                const r = REASON_LABELS[item.reason] || { label: item.reason, type: 'return', color: '#6b7280', bg: '#f9fafb' };
                const isSelected = selected.has(item.id);
                return (
                  <tr key={item.id} style={{ background: isSelected ? '#eff6ff' : idx % 2 === 0 ? '#fff' : '#f9fafb',
                    borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '8px 10px' }}>
                      <input type="checkbox" checked={isSelected} onChange={() => toggleSelect(item.id)} style={{ cursor: 'pointer' }} />
                    </td>
                    <td style={{ padding: '8px 10px', color: '#374151' }}>{formatDate(item.created_at)}</td>
                    <td style={{ padding: '8px 10px' }}>
                      <div style={{ fontWeight: 500, color: '#111827' }}>{item.buyer_login || '—'}</div>
                      {item.buyer_email && <div style={{ fontSize: 11, color: '#9ca3af' }}>{item.buyer_email}</div>}
                    </td>
                    <td style={{ padding: '8px 10px' }}>
                      <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 500,
                        background: r.bg, color: r.color }}>
                        {r.label}
                      </span>
                    </td>
                    <td style={{ padding: '8px 10px', fontSize: 12,
                      color: item.status === 'SUCCESS' ? '#15803d' : item.status === 'PENDING' ? '#d97706' : '#6b7280' }}>
                      {STATUS_LABELS[item.status] || item.status}
                    </td>
                    <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 700, color: '#1d4ed8' }}>
                      {item.total_amount.toFixed(2)} {item.total_currency}
                    </td>
                    <td style={{ padding: '8px 10px', fontSize: 11, color: '#6b7280' }}>{item.account_name}</td>
                    <td style={{ padding: '8px 10px', textAlign: 'center' }}>
                      <button onClick={() => printSingle(item)}
                        style={{ padding: '4px 10px', background: '#f0fdf4', color: '#15803d',
                          border: '1px solid #bbf7d0', borderRadius: 6, fontSize: 11, cursor: 'pointer' }}>
                        🖨️
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Paginacja */}
      {totalPages > 1 && (
        <div style={{ padding: '8px 16px', borderTop: '1px solid #e5e7eb', background: '#fff',
          display: 'flex', alignItems: 'center', gap: 8 }}>
          <button onClick={() => setPage(p => Math.max(0, p-1))} disabled={page === 0}
            style={{ padding: '4px 12px', border: '1px solid #d1d5db', borderRadius: 6, background: 'transparent',
              cursor: page === 0 ? 'not-allowed' : 'pointer', fontSize: 12, color: '#6b7280' }}>←</button>
          <span style={{ fontSize: 12, color: '#6b7280', flex: 1, textAlign: 'center' }}>
            Strona {page+1} z {totalPages} · {total} rekordów
          </span>
          <button onClick={() => setPage(p => p+1)} disabled={page >= totalPages-1}
            style={{ padding: '4px 12px', border: '1px solid #d1d5db', borderRadius: 6, background: 'transparent',
              cursor: page >= totalPages-1 ? 'not-allowed' : 'pointer', fontSize: 12, color: '#6b7280' }}>→</button>
        </div>
      )}
    </div>
  );
}
